import {
    TRANSPORT_LOCATIONS,
    findTransportLocation,
    getAvailableTransportLocations,
} from "@elit-hair/elitklinik-shared";
import { Chip, InputAdornment, TextField } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { GridRenderEditCellParams } from "@mui/x-data-grid";
import { GridApiCommon } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { ChangeEvent, createElement, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbBuilding, TbLocation, TbPlaneInflight } from "react-icons/tb";

import ClipBoard from "@/components/ClipBoard";
import { TableHeader } from "@/components/TableHeader";
import EditAutoCompletete from "@/components/table/EditAutoCompletete";
import MemoizedEditableColumn from "@/components/table/EditableColumn";
import { softColors } from "@/constants/colors";
import { getTransportLocationTypeLabel, transportLocationStyles } from "@/constants/transferLocations";
import { formatFlightNumber } from "@/helpers/strings";
import { usePlanActions, usePlanChangedVal } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";

export const PlanLocationColumn = ({
    t,
    valueGetter,
    editable,
    field,
    headerName,
}: {
    t: TFunction;
    editable: boolean;
    valueGetter: (row: IPlanPassenger<"api">) => string;
    field: string;
    headerName: string;
}): GridColDef<IPlanPassenger<"api">> => {
    return {
        field,
        headerName: t(headerName),
        minWidth: 190,
        flex: 0.5,
        type: "singleSelect",
        editable,
        valueOptions: TRANSPORT_LOCATIONS,
        pastedValueParser: (value: string) => {
            const location = findTransportLocation(value);

            return location?.value || undefined;
        },
        valueGetter: (_val, row) => valueGetter(row),
        renderHeader: (params) => <TableHeader Component={TbLocation} title={params.colDef.headerName!} />,
        renderCell: (params: GridRenderCellParams<IPlanPassenger<"api">>) => (
            <MemoComponent
                editable={editable}
                api={params.api}
                row={params.row}
                field={params.field}
                value={params.value}
            />
        ),
        renderEditCell: (params: GridRenderCellParams) => <EditCell params={params} />,
    };
};

export function PlanFlightNumberCell({ value, colorized = true }: { value: string; colorized?: boolean }) {
    const text = useMemo(() => formatFlightNumber(value), [value]);
    return (
        <ClipBoard text={text}>
            {text ? (
                <Chip
                    sx={{
                        color: colorized ? softColors.indigo.text : undefined,
                        backgroundColor: colorized ? softColors.indigo.bg : undefined,
                        fontWeight: 500,
                        fontSize: "12px !important",
                    }}
                    icon={createElement(TbPlaneInflight, { fontSize: 14, color: softColors.indigo.text })}
                    label={text}
                />
            ) : (
                <div className=" flex h-full w-full  items-center pl-2">-</div>
            )}
        </ClipBoard>
    );
}

export const MemoizedLocationChip = memo(function LocationChip({ value }: { value: string }) {
    const location = useMemo(() => value && findTransportLocation(value), [value]);

    const locationStyle = useMemo(() => {
        if (!location) return null;
        return transportLocationStyles.find((style) => style.value === location.type);
    }, [location]);

    const text = useMemo(() => (location ? location.label : formatFlightNumber(value)), [value, location]);

    return location && locationStyle ? (
        <ClipBoard text={text}>
            <Chip
                sx={{
                    color: locationStyle.text,
                    backgroundColor: locationStyle.bg,
                    fontWeight: 500,
                    fontSize: "12px !important",
                }}
                icon={createElement(locationStyle.component(), {
                    fontSize: 14,
                    color: locationStyle.text,
                })}
                label={text}
            />
        </ClipBoard>
    ) : (
        <PlanFlightNumberCell value={value} />
    );
});

const MemoComponent = memo(
    ({
        value,
        row,
        field,
        api,
        editable,
    }: {
        editable: boolean;
        field: string;
        row: IPlanPassenger<"api">;
        value: string;
        api: GridApiCommon;
    }) => {
        return (
            <MemoizedEditableColumn
                renderHistoryComp={(val) => <MemoizedLocationChip value={val as string} />}
                api={api}
                field={field}
                id={row.id}
                editable={editable}
            >
                <MemoizedLocationChip value={value} />
            </MemoizedEditableColumn>
        );
    }
);

export function EditCell({ params }: { params: GridRenderEditCellParams }) {
    const { processRowUpdate } = usePlanActions();

    const handleUpdate = async (value: string) => {
        processRowUpdate({ ...params.row, [params.colDef.field]: value }, params.row);

        await params.api.setEditCellValue({
            id: params.id,
            field: params.colDef.field,
            value,
        });
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleUpdate(e.target.value || "");
    };

    const { t } = useTranslation();
    const changedAirport = usePlanChangedVal(params.id, "airport");
    const airport = changedAirport || params.row.location;
    const options = useMemo(
        () => getAvailableTransportLocations(params.row.isFlightArrival, !params.row.isFlightArrival),
        [params.row.isFlightArrival]
    );

    return params.row.isFlightArrival ? (
        <TextField
            onClick={(e) => e.stopPropagation()}
            value={params.value}
            onChange={handleInputChange}
            placeholder={t("placeholders.enter", {
                field: t("flight-number"),
            })}
            variant="filled"
            sx={{
                "& .MuiInputBase-input": {
                    padding: "0px 8px",
                    height: "100% !important",
                },
                "& .MuiFilledInput-root": {
                    height: "100%",
                    paddingLeft: 0.5,
                    paddingBottom: 0,
                    paddingTop: 0,
                    fontSize: 12,
                },
            }}
            className="!w-full !h-full "
            InputProps={{
                ...params.InputProps,
                startAdornment: (
                    <InputAdornment position="end">
                        <TbBuilding />
                    </InputAdornment>
                ),
            }}
        />
    ) : (
        <EditAutoCompletete
            processRowUpdate={processRowUpdate}
            renderHistoryComp={(val) => <MemoizedLocationChip value={val as string} />}
            options={options}
            row={params.row}
            field={params.field}
            api={params.api}
            value={params.value}
            groupBy={getTransportLocationTypeLabel}
            optionComp={MemoizedLocationChip}
            icon={TbBuilding}
            excludeValue={airport}
        />
    );
}
